import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { isEqual } from 'lodash';
import { FacetObject, AttributesInterface } from '../../admin-dashboard-tables/interfaces';
import useFilterFacetValues from '../../../../utils/hooks/useFilterFacetValues';
import { DropdownIndicator } from './react-select-custom-child-components/DropdownHelpers';
import AdminValueContainer from './react-select-custom-child-components/AdminValueContainer';
import CheckBoxOption from './react-select-custom-child-components/checkbox-option';

interface Props {
  attributeObject: AttributesInterface,
  facetFilters: Array<string|Array<string>>,
  setFacetFilters: (filters: Array<string|Array<string>>) => void,
  attribute: string,
  max?: number|null,
  andSearch?: boolean,
  addCount?: boolean,
}
// TODO: Separate dropdown into two components, multi- and single-select,
//  cf: https://schweb.atlassian.net/browse/LPBUGS-1355
const AdminSimpleSelectDropdown: React.FC<Props> = ({
  attributeObject,
  facetFilters,
  setFacetFilters,
  attribute,
  max,
  andSearch,
}) => {
  const {
    simpleOptions,
    placeholder,
    isSearchable,
    DisplayComponent,
    isMulti,
  } = attributeObject;
  const [selectedValue, setSelectedValue] = useState<Array<FacetObject>>([]);

  const filterFacetValues = useFilterFacetValues(attribute, facetFilters);

  const onChangeCallback = useCallback((event) => {
    if (event) {
      // Multiselect will return an array
      if (Array.isArray(event)) {
        if (event.length) {
          const newFacets = [...filterFacetValues()];
          if (andSearch) {
            event.forEach((val: { value: string, label: string }) => {
              newFacets.push(`${attribute}:${val.value}`);
            });
          } else {
            newFacets.push(
              event.map((val: { value: string, label: string }) => {
                return `${attribute}:${val.value}`;
              }),
            );
          }
          setFacetFilters(newFacets);
        } else {
          setFacetFilters(filterFacetValues());
        }
      } else {
        // @ts-ignore
        const { value } = event;
        if (value) {
          if (value === 'all') {
            setFacetFilters(filterFacetValues());
          } else {
            setFacetFilters([
              ...filterFacetValues(),
              `${attribute}:${value}`,
            ]);
          }
        }
      }
    } else {
      setFacetFilters(filterFacetValues());
    }
  }, [facetFilters, setFacetFilters, andSearch]);

  useEffect(() => {
    if (facetFilters && facetFilters.length > 0) {
      const newValue: Array<FacetObject> = [];
      const found = facetFilters.filter((item: string | Array<string>) => {
        if (Array.isArray(item)) {
          return item.some((facet: string) => facet.indexOf(attribute) !== -1);
        }
        return item.indexOf(attribute) !== -1;
      });

      if (found) {
        if (Array.isArray(found)) {
          const foundArray = found
            .flat()
            .map((facet: string) => {
              const value = facet.replace(`${attribute}:`, '');
              return {
                label: value,
                value,
              };
            });
          foundArray.forEach((foundObj: FacetObject) => newValue.push(foundObj));
        }
      }
      if (max && newValue.length > max) {
        const truncatedValues = newValue.slice(0, 2);
        onChangeCallback(truncatedValues);
        if (!isEqual(selectedValue, truncatedValues)) {
          setSelectedValue(truncatedValues);
        }
      } else if (!isEqual(selectedValue, newValue)) {
        setSelectedValue(newValue);
      }
    } else if (selectedValue.length > 0) {
      setSelectedValue([]);
    }
  }, [facetFilters, selectedValue, max, andSearch]);

  const isOptionDisabled = useCallback(() => {
    if (!isMulti) {
      return false;
    }
    if (max) {
      return selectedValue.length >= max;
    }
    return false;
  }, [isMulti, max, selectedValue]);

  useEffect(() => {
    if (max && selectedValue.length > max) {
      const newSelectedValue = selectedValue.slice(0, max);
      if (!isEqual(selectedValue, newSelectedValue)) {
        onChangeCallback(newSelectedValue);
      }
    }
  }, [max, selectedValue]);

  if (!simpleOptions) {
    return null;
  }

  return (
    <div className="h-100">
      <Select
        hideSelectedOptions={false}
        isMulti={isMulti}
        isOptionDisabled={isOptionDisabled}
        aria-label={placeholder}
        className="algolia-select btn btn-outline-light border-border-grey button-shadow text-dark-btn-txt align-items-center w-100"
        classNamePrefix="algolia-select"
        value={isMulti ? selectedValue : selectedValue[0]}
        newValueComponent={DisplayComponent}
        components={{
          ValueContainer: AdminValueContainer,
          Option: CheckBoxOption,
          IndicatorSeparator: () => null,
          DropdownIndicator,
        }}
        onChange={onChangeCallback}
        isSearchable={isSearchable}
        options={simpleOptions.map((facetValue) => {
          return {
            label: facetValue,
            value: facetValue,
          };
        })}
        placeholder={placeholder}
        isClearable
      />
    </div>
  );
};

export default AdminSimpleSelectDropdown;
